import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { CopyIndicator } from "./CopyIndicator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { TabsContent } from "../components/ui/tabs";
import { copyId, truncateIdFirstLast } from "../lib/utils";
import { EditPropertyTrigger } from "./modals/EditPropertyTrigger";
import { NewPropertyTrigger } from "./modals/NewPropertyTrigger";
import type { Property, Portfolio } from "global-types";

interface PropertiesProps {
  properties: Property[];
  portfolios: Portfolio[];
  showDetailedView: boolean;
}

const REPORTED_DATASET_URL = "https://console.cloud.google.com/bigquery?ws=!1m4!1m3!3m2!1sreported-461014!2s"
const UNIFIED_DATASET_URL = "https://console.cloud.google.com/bigquery?ws=!1m4!1m3!3m2!1sunified-460313!2s"

export const Properties = ({ properties, portfolios, showDetailedView }: PropertiesProps) => {
  const [activeId, setActiveId] = useState<string | null>(null)

  return (
    <TabsContent value="property" className="mt-4 w-min max-w-full mx-auto">
      <div className="mb-8 space-y-4">
        <NewPropertyTrigger portfolios={portfolios} />
        {properties.length === 0 ? (
          <p>No properties found for this account.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>
                  <div className="inline-flex items-center gap-1">ID</div>
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Property Identifier</TableHead>
                <TableHead>Portfolio Name</TableHead>
                <TableHead>
                  <div className="inline-flex items-center gap-1">
                    Portfolio ID
                  </div>
                </TableHead>
                <TableHead>Statuses</TableHead>
                <TableHead>📊 Reported</TableHead>
                <TableHead>📊 Unified</TableHead>
                {showDetailedView && (
                  <>
                    <TableHead>Address</TableHead>
                    <TableHead>Total Units</TableHead>
                    <TableHead>Year Built</TableHead>
                    <TableHead>Deal Start Date</TableHead>
                    <TableHead>Deal Sold Date</TableHead>
                    <TableHead>Purchase Price</TableHead>
                    <TableHead>Selling Price</TableHead>
                    <TableHead>Image URL</TableHead>
                    <TableHead>Active Tenants</TableHead>
                    <TableHead>Average Rent</TableHead>
                    <TableHead>Latitude</TableHead>
                    <TableHead>Longitude</TableHead>
                    <TableHead>Monthly Revenue</TableHead>
                    <TableHead>Occupancy Rate</TableHead>
                    <TableHead>Hello Data Property ID</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.sort((a, b) => a.property_name.localeCompare(b.property_name)).map((property) => {
                const fullAddress = [
                  property.address,
                  property.city,
                  property.state,
                  property.zip_code,
                ]
                  .filter(Boolean)
                  .join(", ");

                const portfolio = portfolios.find(
                  (p) => p.id === property.portfolio_id,
                );

                return (
                  <TableRow key={property.id}>
                    <TableCell>
                      <EditPropertyTrigger
                        propertyId={property.id}
                        portfolios={portfolios}
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => {
                          copyId(property.id)
                          setActiveId(property.id)
                        }}
                        className="hover:text-blue-600 transition-colors cursor-pointer inline-flex items-center gap-2"
                        id="property-id-copy"
                        title="Click to copy ID"
                      >
                        {truncateIdFirstLast(property.id)}
                        <CopyIndicator activeId={activeId} thisId={property.id} />
                      </button>
                    </TableCell>
                    <TableCell>{property.property_name || "-"}</TableCell>
                    <TableCell>{property.property_identifier || "-"}</TableCell>
                    <TableCell>{portfolio?.name || "-"}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => {
                          setActiveId(property.id.concat(".").concat(property.portfolio_id))
                          copyId(property.portfolio_id)
                        }}
                        className="hover:text-blue-600 transition-colors cursor-pointer inline-flex items-center gap-2"
                        id="portfolio-id-copy"
                        title="Click to copy Portfolio ID"
                      >
                        {truncateIdFirstLast(property.portfolio_id)}
                        <CopyIndicator activeId={activeId} thisId={property.id.concat(".").concat(property.portfolio_id)} />
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-slate">Comps: {`${property.last_comps_status || "unknown"} ${property.last_comps_run_at ? `(${new Date(property.last_comps_run_at).toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" })})` : ""}`}</span>
                        <span className="text-sand">Renewals: {`${property.last_renewals_status || "unknown"} ${property.last_renewals_run_at ? `(${new Date(property.last_renewals_run_at).toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" })})` : ""}`}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <a className="hover:text-blue-600 transition-colors w-full cursor-pointer inline-flex justify-center items-center gap-2" href={`${REPORTED_DATASET_URL}${property.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    <TableCell>
                      <a className="hover:text-blue-600 transition-colors w-full cursor-pointer inline-flex justify-center items-center gap-2" href={`${UNIFIED_DATASET_URL}${property.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    {showDetailedView && (
                      <>
                        <TableCell>{fullAddress || "-"}</TableCell>
                        <TableCell>{property.total_units || "-"}</TableCell>
                        <TableCell>{property.year_built || "-"}</TableCell>
                        <TableCell>
                          {property.deal_start_date
                            ? new Date(property.deal_start_date).toLocaleDateString(
                              "en-US",
                              { year: "numeric", month: "long", day: "numeric" },
                            )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {property.deal_sold_date
                            ? new Date(property.deal_sold_date).toLocaleDateString(
                              "en-US",
                              { year: "numeric", month: "long", day: "numeric" },
                            )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {property.purchase_price
                            ? Number(property.purchase_price).toFixed(3)
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {property.selling_price
                            ? Number(property.selling_price).toFixed(3)
                            : "-"}
                        </TableCell>
                        <TableCell>{property.image_url || "-"}</TableCell>
                        <TableCell>{property.active_tenants || "-"}</TableCell>
                        <TableCell>{property.average_rent || "-"}</TableCell>
                        <TableCell>{property.lat ? Number(property.lat).toFixed(3) : "-"}</TableCell>
                        <TableCell>{property.lon ? Number(property.lon).toFixed(3) : "-"}</TableCell>
                        <TableCell>{property.monthly_revenue || "-"}</TableCell>
                        <TableCell>{property.occupancy_rate || "-"}</TableCell>
                        <TableCell>
                          {property.hello_data_property_id || "-"}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </TabsContent>
  );
};
